/**
 * Medications Hook
 *
 * Provides TanStack Query hooks for managing user_medications via the
 * /api/medications CRUD endpoints.
 *
 * Used by the Medications management tab (T2.6) and future flows.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  createApiClient,
  ApiClientError,
} from "@floline/api-client";
import type {
  UserMedicationResponse,
  CreateMedicationRequest,
  UpdateMedicationRequest,
  SwitchMedicationRequest,
  SwitchMedicationResponse,
  CreateEventRequest,
  ListMedicationsResponse,
} from "@floline/api-client";
import { API_URL } from "@/env";
import { activeMedicationKeys } from "./use-active-medications";
import { sortMedications } from "@/lib/medication-sort-utils";

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const medicationKeys = {
  all: ["medications"] as const,
  lists: () => [...medicationKeys.all, "list"] as const,
  list: (filters: MedicationFilters) =>
    [...medicationKeys.lists(), filters] as const,
};

/** API client configured for medication requests */
const apiClient = createApiClient(
  API_URL
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MedicationFilters {
  status?: string;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// useMedications – list user medications
// ---------------------------------------------------------------------------

/**
 * Hook to fetch user medications with optional status filter.
 *
 * @param filters - Optional filters (status: "active" | "stopped" | "all")
 *
 * @example
 * ```tsx
 * const { medications, isLoading } = useMedications({ status: "all" });
 * ```
 */
export function useMedications(filters?: MedicationFilters) {
  const query = useQuery({
    queryKey: medicationKeys.list(filters ?? {}),
    queryFn: async (): Promise<ListMedicationsResponse> => {
      return await apiClient.listMedications(filters);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount: number, error: Error) => {
      if (
        error instanceof ApiClientError &&
        (error.status === 401 || error.status === 404)
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Apply deterministic client-side sort so the list order depends only on
  // active status → startDate (desc) → displayName (asc).
  // This prevents re-ordering when toggling showInTrends or any other
  // field that updates `updatedAt` but doesn't affect sort keys.
  const medications = useMemo(
    () => sortMedications((query.data?.data ?? []) as UserMedicationResponse[]),
    [query.data],
  );

  return {
    /** Array of user medications (deterministically sorted) */
    medications,
    /** Whether data is currently loading */
    isLoading: query.isLoading,
    /** Whether the fetch failed */
    isError: query.isError,
    /** Error details */
    error: query.error,
    /** Total count from server */
    total: query.data?.meta?.total ?? 0,
    /** Refetch */
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// useCreateMedication – POST /api/medications
// ---------------------------------------------------------------------------

/**
 * Hook to create a new user medication.
 *
 * On success, invalidates medication lists and active-medication caches so
 * the UI refreshes automatically.
 */
export function useCreateMedication() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateMedicationRequest) => {
      const response = await apiClient.createMedication(data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate medication lists and active-medication caches
      queryClient.invalidateQueries({ queryKey: medicationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: activeMedicationKeys.all,
      });
    },
  });

  return {
    createMedication: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useUpdateMedication – PATCH /api/medications/:id
// ---------------------------------------------------------------------------

/**
 * Hook to update a user medication (edit dose, stop, toggle showInTrends, etc.)
 */
export function useUpdateMedication() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateMedicationRequest;
    }) => {
      const response = await apiClient.updateMedication(id, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate medication lists and active-medication caches
      queryClient.invalidateQueries({ queryKey: medicationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: activeMedicationKeys.all,
      });
    },
  });

  return {
    updateMedication: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useSwitchMedication – POST /api/medications/:id/switch
// ---------------------------------------------------------------------------

/**
 * Hook to switch from one medication to another.
 */
export function useSwitchMedication() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: SwitchMedicationRequest;
    }) => {
      const response = await apiClient.switchMedication(id, data);
      return response.data as SwitchMedicationResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medicationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: activeMedicationKeys.all,
      });
    },
  });

  return {
    switchMedication: mutation.mutateAsync,
    isSwitching: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useDeleteMedication – DELETE /api/medications/:id
// ---------------------------------------------------------------------------

/**
 * Hook to soft-delete an inactive user medication.
 *
 * Only allowed when the medication is already stopped (has endDate).
 * On success, invalidates medication lists and active-medication caches.
 */
export function useDeleteMedication() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteMedication(id);
    },
    onSuccess: () => {
      // Invalidate medication lists and active-medication caches
      queryClient.invalidateQueries({ queryKey: medicationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: activeMedicationKeys.all,
      });
    },
  });

  return {
    deleteMedication: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useCreateAdherenceNote – POST /api/events (type medication_adherence_note)
// ---------------------------------------------------------------------------

/**
 * Hook to create a medication_adherence_note event (shortcut from the
 * medication card actions).
 */
export function useCreateAdherenceNote() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateEventRequest) => {
      const response = await apiClient.createEvent(data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate event caches so timeline refreshes
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });

  return {
    createAdherenceNote: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
}
